import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@app/store';
import type { EntityOverride, User, ViewAsContext } from './types';
import { readEntityOverride, readEntityOverrideOwner, writeEntityOverride } from './entityOverrideStorage';
import { readSessionUser, writeSessionUser } from './sessionUserStorage';

export interface AuthState {
  currentUser: User | null;
  viewAs: ViewAsContext | null;
  /** Active entity selected via EntitySelector. Drives query scoping on
   * any page wrapped by that picker. Persisted to localStorage so it
   * survives F5; cleared on sign-out. */
  entityOverride: EntityOverride | null;
  loginStatus: 'idle' | 'loading' | 'error';
  loginError: string | null;
}

const initialState: AuthState = {
  // Rehydrate the signed-in user from sessionStorage so a page reload restores
  // the session without a /v1/refresh round-trip (paired with tokenStore's
  // persisted access token). Cleared on tab close + on sign-out.
  currentUser: readSessionUser(),
  viewAs: null,
  // Rehydrate from localStorage on slice init so the selected merchant
  // survives a page refresh (mirrors portalnew's redux-persist behavior).
  entityOverride: readEntityOverride(),
  loginStatus: 'idle',
  loginError: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Set after a successful login or refresh. Token storage is separate (tokenStore). */
    userAuthenticated(state, action: PayloadAction<User>) {
      // Shared-browser guard: a persisted "view as" override belongs to the
      // user who set it. If a *different* user authenticates, drop the scope
      // so User B never inherits User A's override (the owner id is stamped
      // alongside the override in entityOverrideStorage). Overrides written
      // before this owner-tracking shipped have no owner and clear once.
      const ownerId = readEntityOverrideOwner();
      if (state.entityOverride && ownerId !== action.payload.id) {
        state.entityOverride = null;
        writeEntityOverride(null);
      }
      state.currentUser = action.payload;
      // Persist so a reload restores the session without /v1/refresh.
      writeSessionUser(action.payload);
      state.loginStatus = 'idle';
      state.loginError = null;
    },
    /** Wipe all auth state. Token clearing happens in the saga/thunk that dispatches this. */
    userSignedOut(state) {
      state.currentUser = null;
      writeSessionUser(null);
      state.viewAs = null;
      state.entityOverride = null;
      writeEntityOverride(null);
      state.loginStatus = 'idle';
      state.loginError = null;
    },
    loginStarted(state) {
      state.loginStatus = 'loading';
      state.loginError = null;
    },
    loginFailed(state, action: PayloadAction<string>) {
      state.loginStatus = 'error';
      state.loginError = action.payload;
    },
    viewAsSet(state, action: PayloadAction<ViewAsContext | null>) {
      state.viewAs = action.payload;
    },
    entityOverrideSet(state, action: PayloadAction<EntityOverride | null>) {
      state.entityOverride = action.payload;
      // Stamp the current user as the override's owner so a later sign-in by
      // a different user on the same browser clears it (see userAuthenticated).
      writeEntityOverride(action.payload, state.currentUser?.id ?? null);
    },
  },
});

export const {
  userAuthenticated,
  userSignedOut,
  loginStarted,
  loginFailed,
  viewAsSet,
  entityOverrideSet,
} = authSlice.actions;

export const authReducer = authSlice.reducer;

/* -------------------- Selectors -------------------- */

const selectAuth = (state: RootState): AuthState => state.auth;

export const selectCurrentUser = createSelector(
  [selectAuth],
  (auth) => auth.currentUser,
);

export const selectIsAuthenticated = createSelector(
  [selectAuth],
  (auth) => auth.currentUser !== null,
);

export const selectLoginStatus = createSelector(
  [selectAuth],
  (auth) => auth.loginStatus,
);

export const selectLoginError = createSelector(
  [selectAuth],
  (auth) => auth.loginError,
);

export const selectViewAs = createSelector(
  [selectAuth],
  (auth) => auth.viewAs,
);

export const selectEntityOverride = createSelector(
  [selectAuth],
  (auth) => auth.entityOverride,
);

export const selectCurrentUserRoles = createSelector(
  [selectCurrentUser],
  (user) => user?.roles ?? [],
);

/**
 * Derived permissions index: a map of resourceName → Set of permission names.
 * Computed once per user-state change and memoized by reselect.
 */
export const selectPermissionsByResource = createSelector(
  [selectCurrentUserRoles],
  (roles): Record<string, Set<string>> => {
    const index: Record<string, Set<string>> = {};
    for (const role of roles) {
      for (const set of role.permissionSet) {
        const bucket = index[set.resourceName] ?? new Set<string>();
        for (const perm of set.associatedPermissions) {
          bucket.add(perm.name);
        }
        index[set.resourceName] = bucket;
      }
    }
    return index;
  },
);

export const selectUserAttributes = createSelector(
  [selectCurrentUser],
  (user) => user?.attributes ?? {},
);
